/**
 * SKU stock position, rebuilt on the template's own pieces:
 *
 * vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/order/order-view.tsx
 *   the 8/4 main-and-side grid under a figure strip;
 * vendor/isomorphic/apps/isomorphic/src/app/shared/appointment/appointment-list/stats.tsx
 *   the solid colour icon tiles on the four stock figures;
 * vendor/isomorphic/apps/isomorphic/src/app/shared/social-media/dashboard/recent-activities.tsx
 *   the movement rows;
 * vendor/isomorphic/apps/isomorphic/src/app/shared/logistics/tracking/shipping-info.tsx
 *   the icon tile + label/value blocks in the side cards.
 * The header is `components/detail-parts.tsx`'s `PageHero`. No template stock-position page exists;
 * the figures are 007-inventory's own (spec §16, US1).
 *
 * `FreeStockView` carries only `skuId` (a GUID) — verified against `InventoryViews.cs`: no
 * `skuCode`/`skuName` anywhere on the Warehouse side. So this loader also calls Catalog's
 * `GET /skus/{id}` for a heading better than a raw GUID (the same join `List.tsx`'s row merge needs
 * for its SKU column).
 *
 * T024/US5: movement history. `GET /stock/{skuId}/movements` has no pagination (verified against
 * `InventoryController.cs`), so the whole history arrives at once; the timeline shows the newest few
 * and expands in place, and the stock-movement report is one click away for the tabular view.
 *
 * Every figure is `StockFigure`'s (FR-001 to FR-004, FR-012): the authority statement rides on each
 * one as accessible text and is stated once, visibly, in the header; a negative balance stays
 * negative; a missing conversion factor says why instead of showing 0. The tiles' colours read each
 * figure's sign and nothing more.
 *
 * Only the data and labels are ours. The screen-to-template mapping is
 * specs/017-isomorphic-theme-only/contracts/screen-map.md.
 */
import { useMemo, useState, type ReactNode } from "react";
import { Link, useLoaderData, useNavigate, useOutletContext, type LoaderFunctionArgs } from "react-router-dom";
import {
  PiArrowsDownUpDuotone as MovementIcon,
  PiBarcodeDuotone as BarcodeIcon,
  PiCircleDuotone as NeutralIcon,
  PiCubeDuotone as CubeIcon,
  PiDotsThreeOutlineVerticalFill as MoreIcon,
  PiFileArrowUpDuotone as UploadIcon,
  PiHashDuotone as HsnIcon,
  PiLockSimpleDuotone as ReservedIcon,
  PiPackageDuotone as PiecesIcon,
  PiPencilSimpleDuotone as EditIcon,
  PiRulerDuotone as UnitIcon,
  PiSlidersHorizontalDuotone as AdjustIcon,
  PiStackDuotone as BatchIcon,
  PiStorefrontDuotone as FreeIcon,
  PiTrayArrowDownBold as ReceiveIcon,
  PiTrayArrowDownDuotone as InIcon,
  PiTrayArrowUpDuotone as OutIcon,
  PiWarehouseDuotone as WarehouseIcon,
  PiWarningOctagonDuotone as ExceptionIcon,
} from "react-icons/pi";
import { Alert, Badge, Button, Dropdown, Text } from "rizzui";
import { EmptyState, MetricCard, StockFigure, WidgetCard, cn } from "@steelyard/theme";
import { CardTitle, CopyButton, InfoBlock, PageHero, StatusPill, iconMenuTriggerClass } from "../../components/detail-parts";
import { formatMoment } from "../../lib/format";
import {
  formatQty,
  getStock,
  getStockMovements,
  movementKindLabel,
  movementSourceText,
  personName,
  qty,
  reasonCodeLabel,
  type FreeStockView,
  type MovementHistory,
  type MovementView,
} from "../../lib/services/warehouse";
import { byKey, getSku, getSkuBySlug, type SkuDetail } from "../../lib/services/catalog";
import { inventoryPath, skuPath } from "../../lib/paths";
import { can } from "../../lib/permissions";
import type { PermissionState } from "../../types";
import { AvailabilityBadge } from "./columns";

/**
 * `:skuSlug` is a catch-all under `inventory/*` (routes.tsx lists every static segment first), so a
 * stray path like `/inventory/stock` lands here and is answered by the SKU read's own 404 — the
 * not-found page — before any Warehouse call is made. A legacy GUID URL redirects to the slug one.
 */
export async function inventoryDetailLoader({ params, request }: LoaderFunctionArgs) {
  const sku = await byKey(request, params.skuSlug!, getSku, getSkuBySlug, inventoryPath);
  const [stock, history] = await Promise.all([getStock(sku.id), getStockMovements(sku.id)]);
  return { stock, sku, history };
}

/** Solid tile for a figure: red when the API's figure is negative, the figure's own colour otherwise. */
function tileFor(value: number | string, colour: string): string {
  const parsed = qty(value);
  return cn(parsed !== null && parsed < 0 ? "bg-red" : colour, "text-white shadow-sm");
}

export default function InventoryDetailPage() {
  const { stock, sku, history } = useLoaderData() as { stock: FreeStockView; sku: SkuDetail; history: MovementHistory };
  // Absent outside the app shell (a test renders this route bare) — no context means no admin tools.
  const outlet = useOutletContext<{ permissionState: PermissionState } | undefined>();
  const mayAdminister = outlet ? can(outlet.permissionState, "inventory.administer") : false;
  const navigate = useNavigate();
  const derived = stock.derivedCounts[0];

  return (
    <div className="@container flex flex-col gap-6">
      <PageHero
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Stock", href: "/inventory" }, { name: sku.code }]}
        back={{ href: "/inventory", label: "All stock" }}
        icon={<CubeIcon aria-hidden className="size-9" />}
        title={sku.name}
        description="The portal is the authority for every stock figure on this page — Tally’s own stock figures will drift from these."
        meta={
          <>
            <AvailabilityBadge stock={stock} />
            <Badge variant="outline" rounded="pill" className="gap-1.5 border-muted font-mono text-gray-700">
              <BarcodeIcon aria-hidden className="size-4" />
              {sku.code}
            </Badge>
            <StatusPill status={sku.isActive ? "active" : "deactivated"} />
            <Badge variant="flat" color="info" rounded="pill" className="gap-1.5">
              <WarehouseIcon aria-hidden className="size-4" />
              Main Warehouse
            </Badge>
          </>
        }
        actions={
          <>
            {/* T053's small adjacent fix, kept post-T103 (GAP-1 closed): a direct shortcut to the SKU
                editor from the one other screen that already knows this SKU's id. */}
            <Link to={skuPath(sku.slug)}>
              <Button as="span" variant="outline">
                <EditIcon aria-hidden className="me-1.5 size-4" />
                SKU details
              </Button>
            </Link>
            {mayAdminister ? (
              <Link to="/inventory/receipts">
                <Button as="span">
                  <ReceiveIcon aria-hidden className="me-1.5 size-4" />
                  Receive goods
                </Button>
              </Link>
            ) : null}
            <Dropdown placement="bottom-end">
              <Dropdown.Trigger aria-label={`More stock tools for ${sku.code}`} className={iconMenuTriggerClass}>
                <MoreIcon aria-hidden className="size-4" />
              </Dropdown.Trigger>
              <Dropdown.Menu className="w-64">
                {mayAdminister ? (
                  <>
                    <Dropdown.Item onClick={() => navigate("/inventory/adjustments")} className="gap-2.5 text-sm">
                      <AdjustIcon aria-hidden className="size-4 shrink-0 text-secondary" />
                      Adjust stock
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => navigate("/inventory/uploads")} className="gap-2.5 text-sm">
                      <UploadIcon aria-hidden className="size-4 shrink-0 text-blue" />
                      Bulk upload
                    </Dropdown.Item>
                  </>
                ) : null}
                <Dropdown.Item onClick={() => navigate(`/reports/stock-movement?skuId=${sku.id}`)} className="gap-2.5 text-sm">
                  <MovementIcon aria-hidden className="size-4 shrink-0 text-green-dark" />
                  Movement report for this SKU
                </Dropdown.Item>
                <Dropdown.Item onClick={() => navigate("/inventory/exceptions")} className="gap-2.5 text-sm">
                  <ExceptionIcon aria-hidden className="size-4 shrink-0 text-red-dark" />
                  Stock exceptions
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </>
        }
      />

      {/* ── Stock position: three separate, labelled figures, never one merged "available" ────── */}
      <section aria-label="Stock position" className="grid gap-4 @lg:grid-cols-2 @6xl:grid-cols-4">
        <PositionTile
          title="On hand"
          icon={<WarehouseIcon aria-hidden className="size-6" />}
          tile={tileFor(stock.qtyStockingUnit, "bg-blue")}
          hint="Physically in the warehouse"
          figure={<StockFigure compact label="On hand" value={formatQty(stock.qtyStockingUnit)} unit={stock.stockingUnitCode} />}
        />
        <PositionTile
          title="Reserved"
          icon={<ReservedIcon aria-hidden className="size-6" />}
          tile="bg-secondary text-white shadow-sm"
          hint="Held for open orders until packing completes"
          figure={<StockFigure compact label="Reserved" value={formatQty(stock.reservedQty)} unit={stock.stockingUnitCode} />}
        />
        <PositionTile
          title="Free"
          icon={<FreeIcon aria-hidden className="size-6" />}
          tile={tileFor(stock.freeStock, "bg-green")}
          hint="Available to promise to a new order"
          figure={<StockFigure compact label="Free" value={formatQty(stock.freeStock)} unit={stock.stockingUnitCode} />}
        />
        <PositionTile
          title="Piece equivalent"
          icon={<PiecesIcon aria-hidden className="size-6" />}
          tile="bg-blue-lighter text-blue-dark"
          hint={stock.conversionAvailable && derived ? "Derived by the API from the conversion factor" : undefined}
          figure={
            stock.conversionAvailable && derived ? (
              <StockFigure compact label="Piece equivalent" value={formatQty(derived.quantity)} unit={derived.unitCode} />
            ) : (
              <StockFigure compact label="Piece equivalent" unavailableReason="no conversion factor set for this SKU" />
            )
          }
        />
      </section>

      <div className="grid grid-cols-1 items-start gap-6 @5xl:grid-cols-12">
        <div className="@5xl:col-span-8">
          <MovementTimeline history={history} unitCode={stock.stockingUnitCode} />
        </div>

        <div className="flex flex-col gap-6 @5xl:col-span-4">
          <WidgetCard
            rounded="xl"
            headerClassName="items-center"
            title={
              <CardTitle icon={<BarcodeIcon aria-hidden className="size-5" />} tile="bg-secondary-lighter text-secondary-dark">
                SKU facts
              </CardTitle>
            }
          >
            <div className="mt-5 flex flex-col gap-4">
              <InfoBlock icon={<BarcodeIcon aria-hidden className="size-4" />} tile="bg-secondary-lighter text-secondary-dark" label="SKU code" action={<CopyButton value={sku.code} label="Copy SKU code" />}>
                <Text className="font-mono text-sm text-gray-900">{sku.code}</Text>
              </InfoBlock>
              <InfoBlock icon={<HsnIcon aria-hidden className="size-4" />} tile="bg-orange-lighter text-orange-dark" label="HSN code">
                <Text className="font-mono text-sm text-gray-900">{sku.hsnCode || "Not set"}</Text>
              </InfoBlock>
              <InfoBlock icon={<UnitIcon aria-hidden className="size-4" />} tile="bg-blue-lighter text-blue-dark" label="Stocked in">
                <Text className="text-sm text-gray-900">{stock.stockingUnitCode}</Text>
              </InfoBlock>
              <InfoBlock icon={<PiecesIcon aria-hidden className="size-4" />} tile="bg-green-lighter text-green-dark" label="Sellable units">
                {sku.units.length > 0 ? (
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {sku.units.map((unit) => (
                      <Badge key={unit.unitId} variant="flat" color={unit.isPrimary ? "success" : "info"} size="sm" rounded="pill">
                        {unit.unitCode}
                        {unit.isPrimary ? " · primary" : ""}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <Text className="text-sm text-gray-500">None declared</Text>
                )}
              </InfoBlock>
              <InfoBlock icon={<BatchIcon aria-hidden className="size-4" />} tile="bg-gray-100 text-gray-600" label="Batches">
                <Text className="text-sm text-gray-900">{sku.batchTracked ? "Tracked by batch" : "Not batch tracked"}</Text>
              </InfoBlock>
            </div>
          </WidgetCard>

          <WidgetCard
            rounded="xl"
            headerClassName="items-center"
            title={
              <CardTitle icon={<WarehouseIcon aria-hidden className="size-5" />} tile="bg-blue-lighter text-blue-dark">
                Where it is stocked
              </CardTitle>
            }
          >
            <div className="mt-5 flex flex-col gap-4">
              <InfoBlock icon={<WarehouseIcon aria-hidden className="size-4" />} tile="bg-blue-lighter text-blue-dark" label="Warehouse">
                <Text className="text-sm font-medium text-gray-900">Main Warehouse</Text>
              </InfoBlock>
              {/* FR-005/D-22: named once here, not a selector. */}
              <Alert color="info" variant="flat">
                <Text className="text-sm">
                  This is the single v1 warehouse. A location selector is not offered — it would imply a capability this release does not have.
                </Text>
              </Alert>
            </div>
          </WidgetCard>
        </div>
      </div>
    </div>
  );
}

function PositionTile({ title, icon, tile, hint, figure }: { title: string; icon: ReactNode; tile: string; hint?: string; figure: ReactNode }) {
  return (
    <MetricCard
      title={title}
      metric={figure}
      icon={icon}
      iconClassName={tile}
      metricClassName="[&_.font-mono]:text-2xl"
      info={hint ? <Text className="mt-0.5 text-xs text-gray-500">{hint}</Text> : null}
      rounded="lg"
      className="transition-shadow hover:shadow-md"
    />
  );
}

/* ── Movement history ─────────────────────────────────────────────────────────────────────── */

const PREVIEW = 8;

/** A movement's direction, read from the sign of the API's own deltas — never recomputed. */
function directionOf(movement: MovementView): { tile: string; figure: string; icon: ReactNode } {
  const change = qty(movement.qtyDelta) ?? 0;
  if (change > 0) return { tile: "bg-green-lighter text-green-dark", figure: "text-green-dark", icon: <InIcon aria-hidden className="size-4" /> };
  if (change < 0) return { tile: "bg-red-lighter text-red-dark", figure: "text-red-dark", icon: <OutIcon aria-hidden className="size-4" /> };
  if ((qty(movement.reservedDelta) ?? 0) !== 0) return { tile: "bg-secondary-lighter text-secondary-dark", figure: "text-secondary-dark", icon: <ReservedIcon aria-hidden className="size-4" /> };
  return { tile: "bg-gray-100 text-gray-500", figure: "text-gray-500", icon: <NeutralIcon aria-hidden className="size-4" /> };
}

function MovementTimeline({ history, unitCode }: { history: MovementHistory; unitCode: string }) {
  // Ordering for display only — a sort by timestamp, not a recomputed value (constitution I).
  const sorted = useMemo(() => [...history.movements].sort((a, b) => b.occurredAt.localeCompare(a.occurredAt)), [history.movements]);
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? sorted : sorted.slice(0, PREVIEW);

  return (
    <WidgetCard
      rounded="xl"
      headerClassName="items-center"
      title={
        <CardTitle icon={<MovementIcon aria-hidden className="size-5" />} tile="bg-orange-lighter text-orange-dark">
          <span className="flex items-center gap-2">
            Movement history
            {sorted.length > 0 ? (
              <Badge variant="flat" color="warning" rounded="pill" size="sm">
                {sorted.length}
              </Badge>
            ) : null}
          </span>
        </CardTitle>
      }
      action={
        <Link to={`/reports/stock-movement?skuId=${history.skuId}`}>
          <Button as="span" variant="text" size="sm">
            Full report for this SKU
          </Button>
        </Link>
      }
    >
      {sorted.length === 0 ? (
        <div className="pt-5">
          <EmptyState title="No movements yet">
            Every receipt, decrement, adjustment, reversal and reservation change for this SKU will list here once one is recorded, each with its cause named.
          </EmptyState>
        </div>
      ) : (
        <>
          <ol aria-label="Stock movements for this SKU, most recent first" className="mt-6 flex flex-col">
            {visible.map((movement, index) => {
              const direction = directionOf(movement);
              const change = qty(movement.qtyDelta) ?? 0;
              const reservedChange = qty(movement.reservedDelta) ?? 0;
              return (
                <li key={movement.id} className="group relative flex gap-4 pb-5 last:pb-0">
                  {index < visible.length - 1 ? <span aria-hidden className="absolute bottom-0 start-[17px] top-10 w-0.5 bg-muted" /> : null}
                  <span className={cn("relative z-10 flex size-9 shrink-0 items-center justify-center rounded-full ring-4 ring-gray-0 dark:ring-gray-50", direction.tile)}>
                    {direction.icon}
                  </span>
                  <div className="flex min-w-0 flex-1 flex-wrap items-start justify-between gap-x-4 gap-y-1.5 rounded-lg px-3 py-1.5 transition-colors group-hover:bg-gray-50 dark:group-hover:bg-gray-100/40">
                    <div className="flex min-w-0 flex-col gap-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold text-gray-900">{movementKindLabel(movement.kind)}</span>
                        {movement.reasonCode ? (
                          <Badge variant="flat" size="sm" rounded="pill" className="bg-gray-100 text-gray-600">
                            {reasonCodeLabel(movement.reasonCode)}
                          </Badge>
                        ) : null}
                      </div>
                      <span className="text-xs text-gray-500">
                        {movementSourceText(movement)}
                        {movement.actorId ? ` · by ${personName(movement.actorName)}` : null}
                      </span>
                      {movement.note ? <Text className="mt-1 rounded-md border-s-2 border-muted bg-gray-50 px-3 py-1.5 text-sm italic text-gray-700 dark:bg-gray-100/40">“{movement.note}”</Text> : null}
                    </div>
                    <div className="flex flex-col items-end gap-0.5 text-right">
                      {change !== 0 ? (
                        <span className={cn("font-mono text-base font-semibold tabular-nums", direction.figure)}>
                          {change > 0 ? "+" : ""}
                          {formatQty(movement.qtyDelta)} {unitCode}
                        </span>
                      ) : null}
                      {reservedChange !== 0 ? (
                        <span className="font-mono text-xs tabular-nums text-secondary-dark">
                          {formatQty(movement.reservedDelta)} {unitCode} reserved
                        </span>
                      ) : null}
                      {change === 0 && reservedChange === 0 ? <span className="text-xs text-gray-500">No quantity change</span> : null}
                      <span className="text-xs text-gray-500">{formatMoment(movement.occurredAt)}</span>
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
          {sorted.length > PREVIEW ? (
            <div className="mt-4 flex justify-center border-t border-dashed border-muted pt-4">
              <Button type="button" variant="text" size="sm" onClick={() => setShowAll((open) => !open)}>
                {showAll ? "Show the latest only" : `Show all ${sorted.length} movements`}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </WidgetCard>
  );
}
